/**
 * Iter XX.199 — <SecondOpenCard/>
 *
 * The lightweight "Gully remembers me" surface. Fires ONCE per identity
 * for their lifetime (server-enforced). Not a modal, not a blocker —
 * sits inline above the composer and disappears the moment the player
 * starts typing.
 *
 * Dumb component — all decisions (show / don't show, sentence, prior
 * session id) are made server-side by /web/second-open-context.
 *
 * data-testids (kept surgical for the QA regression suite):
 *   · second-open-card
 *   · second-open-continue
 *   · second-open-new-topic
 *   · second-open-salutation
 *   · second-open-sentence
 */
import React from "react";

/**
 * @param {object} props
 * @param {string} props.salutation             "Welcome back, Shiv."
 * @param {string} props.sentence               personalized one-liner
 * @param {() => void} props.onContinue         hydrate + switch to prior session
 * @param {() => void} props.onNewTopic         dismiss card (keep current session)
 */
export default function SecondOpenCard({ salutation, sentence, onContinue, onNewTopic }) {
  return (
    <div
      data-testid="second-open-card"
      className="mx-auto max-w-2xl px-4"
    >
      <div
        className="mt-3 mb-4 rounded-2xl border border-neutral-800/70 bg-neutral-950/70 backdrop-blur-sm px-5 py-4 shadow-lg shadow-black/20"
        role="region"
        aria-label="Welcome back"
      >
        <div
          data-testid="second-open-salutation"
          className="text-neutral-100 text-base font-medium leading-snug"
        >
          {salutation}
        </div>
        {sentence ? (
          <div
            data-testid="second-open-sentence"
            className="mt-1.5 text-neutral-400 text-sm leading-relaxed"
          >
            {sentence}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            data-testid="second-open-continue"
            onClick={onContinue}
            className="w-full text-left rounded-xl bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/25 text-emerald-200 px-4 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
          >
            Continue where we left off
          </button>
          <button
            type="button"
            data-testid="second-open-new-topic"
            onClick={onNewTopic}
            className="w-full text-left rounded-xl border border-neutral-800/80 hover:border-neutral-700 text-neutral-300 hover:text-neutral-100 px-4 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500/60"
          >
            Start a new topic
          </button>
        </div>
      </div>
    </div>
  );
}
